import { useState, useEffect, useRef, useCallback } from 'react';
import {
  BUDDY_EVENTS, EVENT_DESCRIPTIONS, BUDDY_TUNING, RARITIES,
  EGG_ASCII, DEAD_ASCII, RETIRE_ASCII,
  getBuddyById, pickRandomBuddyId, pickFallback, elapsedWeekdayMs, getTenureMs,
} from '../buddy';
import { api } from '../api';
import ChatBot from './ChatBot';

const STORAGE_KEY = 'bidboard-buddy-state';

function now() { return Date.now(); }

// localStorage is a write-through cache for offline resilience;
// the SQL UserBuddyState table is the source of truth.
function loadCache() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw);
  } catch (e) {}
  return null;
}

function saveCache(s) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(s)); } catch (e) {}
}

function newEggState() {
  return {
    id: pickRandomBuddyId(),
    stage: 'egg',
    hatchClicks: 0,
    lastFedAt: null,
    recentFeeds: [],
    diedAt: null,
    hatchedAt: null,
    nextEggAt: null,
    exitReason: null,
  };
}

/**
 * Apply time-based transitions. Returns { next, retired } — retired is true
 * if this tick is the one that moved the buddy from alive/fat → dead/retired,
 * so the caller can fire the farewell quip exactly once.
 */
function tick(state, buddy) {
  if (!state) return { next: newEggState() };
  const t = now();

  // Dead/retired: stay put until user clicks to reset to egg
  if (state.stage === 'dead') return { next: state };

  // Alive or fat: check tenure first (retirement beats starvation), then starvation
  if (state.stage === 'alive' || state.stage === 'fat') {
    const hatched = state.hatchedAt || t;
    const tenureMs = buddy ? getTenureMs(buddy) : 3 * 24 * 60 * 60 * 1000;
    if (elapsedWeekdayMs(hatched, t) > tenureMs) {
      return {
        next: { ...state, stage: 'dead', exitReason: 'retired', diedAt: t },
        retired: true,
      };
    }
    const lastFed = state.lastFedAt || hatched;
    if (elapsedWeekdayMs(lastFed, t) > BUDDY_TUNING.DEATH_NO_FEED_MS) {
      return {
        next: { ...state, stage: 'dead', exitReason: 'starved', diedAt: t },
      };
    }
    // Fat recovery
    if (state.stage === 'fat') {
      const recent = (state.recentFeeds || []).filter(ts => t - ts < BUDDY_TUNING.FAT_RECOVERY_MS);
      if (recent.length === 0) {
        return { next: { ...state, stage: 'alive', recentFeeds: [] } };
      }
    }
  }

  return { next: state };
}

export default function BuddyWidget() {
  const [state, setState] = useState(() => loadCache() || newEggState());
  const [quip, setQuip] = useState(null);
  const [bubbleType, setBubbleType] = useState(null); // 'quip' | 'nom' | 'crack' | 'notification'
  const [chatOpen, setChatOpen] = useState(false);
  const lastQuipAt = useRef(0);
  const stageRef = useRef(state.stage);
  const hasLoadedFromServer = useRef(false);
  const pendingSave = useRef(null);
  useEffect(() => { stageRef.current = state.stage; }, [state.stage]);

  // Cache on change + debounced write-through to backend
  useEffect(() => {
    saveCache(state);
    if (!hasLoadedFromServer.current) return;
    clearTimeout(pendingSave.current);
    pendingSave.current = setTimeout(() => {
      api.saveBuddyState(state).catch(() => {});
    }, 600);
  }, [state]);

  // On mount and on window focus: fetch from server (catches admin overrides)
  useEffect(() => {
    async function hydrate() {
      try {
        const server = await api.getBuddyState();
        if (server && server.id && server.stage) {
          hasLoadedFromServer.current = true;
          setState({
            id: server.id,
            stage: server.stage,
            hatchClicks: server.hatchClicks || 0,
            lastFedAt: server.lastFedAt || null,
            recentFeeds: server.recentFeeds || [],
            hatchedAt: server.hatchedAt || null,
            diedAt: server.diedAt || null,
            nextEggAt: server.nextEggAt || null,
            exitReason: server.exitReason || null,
          });
        } else {
          const fresh = newEggState();
          hasLoadedFromServer.current = true;
          setState(fresh);
          api.saveBuddyState(fresh).catch(() => {});
        }
      } catch (e) {
        hasLoadedFromServer.current = true;
      }
    }
    hydrate();
    window.addEventListener('focus', hydrate);
    return () => window.removeEventListener('focus', hydrate);
  }, []);

  const buddy = getBuddyById(state.id);
  const rarity = RARITIES[buddy.rarity] || RARITIES.common;

  const showBubble = useCallback((text, type = 'quip') => {
    setQuip(text);
    setBubbleType(type);
    clearTimeout(showBubble._t);
    showBubble._t = setTimeout(() => setQuip(null), 10000);
  }, []);

  // AI-driven quip — falls back to pool if the API isn't available.
  const fetchQuip = useCallback(async (eventType, ctx) => {
    const stage = stageRef.current;
    if (stage === 'egg' || stage === 'dead') return;

    // Rate-limit spontaneous quips; allow poke/feed/hatch/retire anytime
    const privileged = [BUDDY_EVENTS.PET, 'buddy:fed', 'buddy:overfed', 'buddy:hatched', 'buddy:retiring'];
    if (!privileged.includes(eventType)) {
      if (now() - lastQuipAt.current < BUDDY_TUNING.MIN_BUBBLE_GAP_MS) return;
    }
    lastQuipAt.current = now();

    const descBuilder = EVENT_DESCRIPTIONS[eventType];
    const description = typeof descBuilder === 'function' ? descBuilder(ctx) : descBuilder;
    if (!description) return;

    let text = null;
    try {
      const res = await api.buddyQuip(buddy.name, buddy.bio, description);
      text = res?.quip || null;
    } catch (e) { /* swallow */ }
    if (!text) text = pickFallback(buddy);
    showBubble(text, 'quip');
  }, [buddy, showBubble]);

  // Run the time-tick every 30s AND on focus — for starvation + retirement
  useEffect(() => {
    function applyTick() {
      setState(prev => {
        const { next, retired } = tick(prev, getBuddyById(prev.id));
        if (next === prev) return prev;
        if (retired) {
          // Fire farewell once — in character
          const farewellCtx = { buddyName: getBuddyById(prev.id).name };
          // small delay so the state update lands before the bubble
          setTimeout(() => {
            lastQuipAt.current = 0; // bypass debounce
            const savedStage = stageRef.current;
            stageRef.current = 'alive'; // trick the quip fn into speaking
            fetchQuip('buddy:retiring', farewellCtx).finally(() => {
              stageRef.current = savedStage;
            });
          }, 50);
        }
        return next;
      });
    }
    applyTick();
    const id = setInterval(applyTick, 30000);
    window.addEventListener('focus', applyTick);
    return () => { clearInterval(id); window.removeEventListener('focus', applyTick); };
  }, [fetchQuip]);

  // Notification polling removed — notifications now live in the dashboard card.


  // Close chat if buddy is no longer alive/fat
  useEffect(() => {
    if (state.stage !== 'alive' && state.stage !== 'fat' && chatOpen) {
      setChatOpen(false);
    }
  }, [state.stage, chatOpen]);

  // Subscribe to BidBoard events (existing quip triggers)
  useEffect(() => {
    const handlers = {};
    Object.values(BUDDY_EVENTS).forEach(eventType => {
      handlers[eventType] = (e) => fetchQuip(eventType, e?.detail);
      window.addEventListener(eventType, handlers[eventType]);
    });
    return () => Object.keys(handlers).forEach(t => window.removeEventListener(t, handlers[t]));
  }, [fetchQuip]);

  // ── Interactions ─────────────────────────────────────────────
  function handleLeftClick(e) {
    e.preventDefault();

    // Dead/retired → fresh egg, no lockout
    if (state.stage === 'dead') {
      setState(newEggState());
      showBubble('', 'crack');
      return;
    }

    // Egg → hatch immediately and open chat
    if (state.stage === 'egg') {
      const hatched = { ...state, stage: 'alive', hatchClicks: 0, hatchedAt: now(), lastFedAt: now(), exitReason: null };
      setState(hatched);
      showBubble('', 'crack');
      setChatOpen(true);
      setTimeout(() => fetchQuip('buddy:hatched'), 400);
      return;
    }

    // Alive or fat: toggle chat
    setChatOpen(o => !o);
  }

  function handleRightClick(e) {
    e.preventDefault();
    if (state.stage === 'egg' || state.stage === 'dead') return;

    const t = now();
    const recent = [...(state.recentFeeds || []), t].filter(ts => t - ts < BUDDY_TUNING.FAT_WINDOW_MS);
    const isOverfed = recent.length > BUDDY_TUNING.FAT_FEEDS_IN_WINDOW;
    const newStage = isOverfed ? 'fat' : 'alive';

    setState({ ...state, lastFedAt: t, recentFeeds: recent, stage: newStage });
    showBubble('om nom', 'nom');
    setTimeout(() => fetchQuip(isOverfed ? 'buddy:overfed' : 'buddy:fed'), 500);
  }

  // Render the sprite based on stage + exitReason
  let sprite = buddy.ascii;
  let stageLabel = null;
  if (state.stage === 'egg') {
    sprite = EGG_ASCII;
  } else if (state.stage === 'dead') {
    sprite = state.exitReason === 'retired' ? RETIRE_ASCII : DEAD_ASCII;
    stageLabel = state.exitReason === 'retired' ? 'sent off' : 'rip';
  } else if (state.stage === 'fat') {
    sprite = buddy.ascii.split('\n').map(line => ' ' + line.trim() + ' ').join('\n');
    stageLabel = 'full';
  }

  const nameToShow = state.stage === 'egg'
    ? '?'
    : state.stage === 'dead'
      ? (state.exitReason === 'retired' ? '👷' : '✝')
      : buddy.name;
  const typeLabel = (state.stage === 'alive' || state.stage === 'fat') ? `The ${buddy.type}` : null;

  return (
    <>
      <div
        className={`buddy-widget buddy-stage-${state.stage}`}
        style={{ '--buddy-rarity-color': rarity.color, '--buddy-rarity-label': rarity.label }}
      >
        {quip !== null && (
          <div className={`buddy-bubble buddy-bubble-${bubbleType}`}>
            {bubbleType === 'crack' ? '✨' : quip}
          </div>
        )}
        <pre
          className="buddy-sprite"
          onClick={handleLeftClick}
          onContextMenu={handleRightClick}
          title=""
        >{sprite}</pre>
        <div className="buddy-name">
          {nameToShow}
          {typeLabel && <span className="buddy-type-label">{typeLabel}</span>}
          {stageLabel && <span className="buddy-stage-label">{stageLabel}</span>}
        </div>
      </div>
      {chatOpen && (state.stage === 'alive' || state.stage === 'fat') && (
        <ChatBot buddy={buddy} onClose={() => setChatOpen(false)} />
      )}
    </>
  );
}
